// AI Features, Suggestions, Audit, and Application Search APIs
import { getApiClient, withOrgConfig } from '../quote-api-helpers'
import type {
  AISuggestion, AIDescriptionSuggestion,
  QuoteAuditResult, SimilarQuotesResult,
  AIActionsResult, PricingOptimizationResult, CoverageCheckResult,
  Application,
} from '../quote-api-ai-types'
import type { PriceSuggestionResponse } from '../quote-api-assembly-types'
import type { QuoteLineItem } from '../quote-api-types'

export const aiFeaturesApi = {
  // =========================================================================
  // AI-POWERED FEATURES
  // =========================================================================

  async getAISuggestions(query: string): Promise<AISuggestion[]> {
    try {
      const client = getApiClient()
      const res = await client.get<{ suggestions: AISuggestion[] }>(
        `/quotebuilder/ai/suggestions/`,
        withOrgConfig({ params: { query } })
      )
      return res.data.suggestions || []
    } catch (e) {
      // AI is optional - return empty on failure
      console.warn('AI suggestions unavailable:', e)
      return []
    }
  },

  /**
   * Get comprehensive AI-powered price suggestion with trend analysis
   */
  async getAISuggestion(params: {
    description: string
    unit?: string
    quantity?: number
    quoteId?: string
    sectionId?: string
  }): Promise<PriceSuggestionResponse | null> {
    try {
      const client = getApiClient()
      const res = await client.post<PriceSuggestionResponse>(
        `/quotebuilder/ai/price-suggestion/`,
        {
          description: params.description,
          unit: params.unit || 'm2',
          quantity: params.quantity || 100,
          quote_id: params.quoteId,
          section_id: params.sectionId,
        },
        withOrgConfig()
      )
      return res.data
    } catch (e) {
      console.warn('AI suggestion unavailable:', e)
      return null
    }
  },

  /**
   * Submit feedback on AI suggestion (accepted/rejected/modified)
   */
  async submitAIFeedback(suggestionId: string, action: string, modifications?: Record<string, unknown>): Promise<boolean> {
    try {
      const client = getApiClient()
      await client.post(
        `/quotebuilder/ai/feedback/`,
        {
          suggestion_id: suggestionId,
          action,
          modifications,
        },
        withOrgConfig()
      )
      return true
    } catch (e) {
      console.warn('Failed to submit AI feedback:', e)
      return false
    }
  },

  async getDescriptionSuggestions(partial: string): Promise<AIDescriptionSuggestion[]> {
    if (!partial || partial.length < 3) return []
    try {
      const client = getApiClient()
      const res = await client.get<{ suggestions: AIDescriptionSuggestion[] }>(
        `/quotebuilder/ai/describe/`,
        withOrgConfig({ params: { q: partial } })
      )
      return res.data.suggestions || []
    } catch (e) {
      console.warn('Description suggestions unavailable:', e)
      return []
    }
  },

  /**
   * @deprecated Use submitAIFeedback instead
   */
  async recordAIFeedback(suggestionId: string, action: 'accepted' | 'rejected' | 'modified', modifiedValue?: string) {
    return aiFeaturesApi.submitAIFeedback(suggestionId, action, modifiedValue ? { modified_value: modifiedValue } : undefined)
  },

  // =========================================================================
  // AI INTELLIGENCE
  // =========================================================================

  async runQuoteAudit(quoteId: string): Promise<QuoteAuditResult> {
    const client = getApiClient()
    const res = await client.post<QuoteAuditResult>(`/quotebuilder/ai/audit/${quoteId}/`, {}, withOrgConfig())
    return res.data
  },

  async findSimilarQuotes(quoteId: string, topK = 5): Promise<SimilarQuotesResult> {
    const client = getApiClient()
    const res = await client.get<SimilarQuotesResult>(
      `/quotebuilder/ai/similar/${quoteId}/`,
      withOrgConfig({ params: { top_k: topK } })
    )
    return res.data
  },

  async getAIActions(quoteId: string): Promise<AIActionsResult> {
    const client = getApiClient()
    const res = await client.get<AIActionsResult>(`/quotebuilder/ai/actions/${quoteId}/`, withOrgConfig())
    return res.data
  },

  async optimizePricing(quoteId: string): Promise<PricingOptimizationResult> {
    const client = getApiClient()
    const res = await client.post<PricingOptimizationResult>(`/quotebuilder/ai/optimize/${quoteId}/`, {}, withOrgConfig())
    return res.data
  },

  async checkCoverage(quoteId: string): Promise<CoverageCheckResult> {
    const client = getApiClient()
    const res = await client.get<CoverageCheckResult>(`/quotebuilder/ai/coverage/${quoteId}/`, withOrgConfig())
    return res.data
  },

  // =========================================================================
  // APPLICATION SEARCH & AI ASSIST
  // =========================================================================

  async searchApplications(query: string): Promise<Application[]> {
    if (!query || query.length < 2) return []
    const client = getApiClient()
    const res = await client.get<Application[]>(
      `/quotebuilder/applications/`,
      withOrgConfig({ params: { search: query, limit: 20 } })
    )
    return res.data
  },

  /**
   * Get AI suggestions for a specific line item.
   * Different from getAISuggestions(query) which does general search.
   */
  async getLineItemAISuggestions(
    quoteId: string,
    itemId: string
  ): Promise<AISuggestion[]> {
    try {
      const client = getApiClient()
      const res = await client.get<AISuggestion[]>(
        `/quotebuilder/quotes/${quoteId}/items/${itemId}/ai-suggestions/`,
        withOrgConfig()
      )
      return res.data
    } catch {
      return []
    }
  },

  /**
   * Run AI audit on a quote to find issues.
   * Uses the dedicated ai/audit endpoint.
   */
  async runAIAudit(quoteId: string): Promise<{
    issues: Array<{
      type: 'coverage' | 'pricing' | 'consistency'
      severity: 'warning' | 'error' | 'info'
      title: string
      description: string
      affected_items: string[]
      suggestion?: string
    }>
  }> {
    const client = getApiClient()
    const res = await client.post(
      `/quotebuilder/quotes/${quoteId}/ai-audit/`,
      {},
      withOrgConfig()
    )
    return res.data
  },

  async getOptimizationSuggestions(quoteId: string): Promise<{
    suggestions: Array<{
      type: 'cost' | 'margin' | 'efficiency'
      title: string
      description: string
      potential_savings?: number
      affected_items: string[]
      action?: {
        type: string
        params: Record<string, unknown>
      }
    }>
  }> {
    const client = getApiClient()
    const res = await client.get(
      `/quotebuilder/quotes/${quoteId}/optimize/`,
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Submit context-aware AI feedback with quote context.
   * Use this when you have quote context available.
   */
  async submitQuoteAIFeedback(
    quoteId: string,
    suggestionId: string,
    feedback: 'accepted' | 'rejected' | 'modified',
    notes?: string
  ): Promise<void> {
    const client = getApiClient()
    await client.post(
      `/quotebuilder/ai-feedback/`,
      {
        quote_id: quoteId,
        suggestion_id: suggestionId,
        feedback,
        notes,
      },
      withOrgConfig()
    )
  },

  async applyApplicationToItem(
    quoteId: string,
    sectionId: string,
    itemId: string,
    applicationId: string
  ): Promise<QuoteLineItem> {
    const client = getApiClient()
    const res = await client.post<QuoteLineItem>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/${itemId}/apply-application/`,
      { application_id: applicationId },
      withOrgConfig()
    )
    return res.data
  },
}
